import { createFileRoute, Link, notFound } from "@tanstack/react-router";
import { ArrowRight, Flame, Scale } from "lucide-react";
import { Button } from "@/components/ui/button";
import { getMpComparison, MP_COMPARISONS } from "@/lib/mp-compare";
import { MP_GUIDES } from "@/lib/mp-guides";
import { MARKETPLACE_OG_IMAGE, MARKETPLACE_OG_IMAGE_ALT, SITE_URL, buildPageMetadata } from "@/lib/seo";
import { useT } from "@/i18n";

export const Route = createFileRoute("/compare/marketplace/$competitor")({
  loader: ({ params }) => {
    const cmp = getMpComparison(params.competitor);
    if (!cmp) throw notFound();
    return { slug: cmp.slug };
  },
  head: ({ params }) => {
    const cmp = getMpComparison(params.competitor);
    if (!cmp) {
      return buildPageMetadata({
        path: `/compare/marketplace/${params.competitor}`,
        title: "Comparison not found | BeHot.Now",
        description: "This comparison is not available on BeHot.Now.",
        noindex: true,
      });
    }
    return buildPageMetadata({
      path: `/compare/marketplace/${cmp.slug}`,
      title: cmp.title,
      description: cmp.description,
      ogTitle: cmp.h1,
      ogDescription: cmp.description,
      image: MARKETPLACE_OG_IMAGE,
      imageAlt: MARKETPLACE_OG_IMAGE_ALT,
      keywords: cmp.keywords,
      breadcrumbs: [
        { name: "Home", path: "/" },
        { name: "Marketplace", path: "/marketplace" },
        { name: `vs ${cmp.brand}`, path: `/compare/marketplace/${cmp.slug}` },
      ],
      jsonLd: {
        "@context": "https://schema.org",
        "@type": "WebPage",
        name: cmp.h1,
        url: `${SITE_URL}/compare/marketplace/${cmp.slug}`,
        description: cmp.description,
        isPartOf: { "@type": "WebSite", url: SITE_URL, name: "BeHot.Now" },
      },
    });
  },
  component: MpComparePage,
});

function MpComparePage() {
  const { competitor } = Route.useParams();
  const { t } = useT();
  const c = getMpComparison(competitor);

  if (!c) {
    return (
      <div className="mx-auto max-w-3xl px-4 py-20 text-center">
        <h1 className="font-display text-2xl font-bold">{t("mpseo.notFound")}</h1>
        <Button asChild variant="hot" className="mt-4">
          <Link to="/compare/marketplace">{t("mpseo.comparisons")}</Link>
        </Button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <nav aria-label="Breadcrumb" className="text-xs text-muted-foreground">
        <Link to="/" className="hover:text-foreground">
          {t("mpseo.home")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/marketplace" className="hover:text-foreground">
          {t("mpseo.marketplace")}
        </Link>
        <span className="px-1.5">/</span>
        <Link to="/compare/marketplace" className="hover:text-foreground">
          {t("mpseo.comparisons")}
        </Link>
      </nav>

      <header className="mt-4">
        <span className="inline-flex items-center gap-1.5 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-primary">
          <Scale className="size-3.5" /> {t("mpseo.comparison")}
        </span>
        <h1 className="mt-3 font-display text-3xl font-bold leading-tight sm:text-4xl">{c.h1}</h1>
        <p className="mt-4 text-base leading-relaxed text-muted-foreground">{c.intro}</p>
      </header>

      <div className="mt-6 flex flex-wrap gap-3">
        <Button asChild variant="hot">
          <Link to="/marketplace">
            {t("mpseo.cta.open")} <ArrowRight className="ml-1 size-4" />
          </Link>
        </Button>
        <Button asChild variant="outline">
          <Link to="/marketplace/sell">{t("mpseo.cta.sell")}</Link>
        </Button>
      </div>

      <div className="mt-8 space-y-4">
        {c.summary.map((p, i) => (
          <p key={i} className="leading-relaxed text-muted-foreground">
            {p}
          </p>
        ))}
      </div>

      <section className="mt-10">
        <h2 className="font-display text-xl font-bold sm:text-2xl">BeHot.Now vs {c.brand}</h2>
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[36rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-left">
                <th scope="col" className="py-2 pr-4 font-semibold">
                  {t("mpseo.dimension")}
                </th>
                <th scope="col" className="py-2 pr-4 font-semibold text-primary">
                  <span className="inline-flex items-center gap-1">
                    <Flame className="size-4" /> BeHot.Now
                  </span>
                </th>
                <th scope="col" className="py-2 font-semibold">
                  {c.brand}
                </th>
              </tr>
            </thead>
            <tbody>
              {c.rows.map((row, i) => (
                <tr key={i} className="border-b border-border/60 align-top">
                  <th scope="row" className="py-3 pr-4 text-left font-medium">
                    {row.dimension}
                  </th>
                  <td className="py-3 pr-4 text-muted-foreground">{row.behot}</td>
                  <td className="py-3 text-muted-foreground">{row.them}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {c.source && (
          <p className="mt-3 text-xs text-muted-foreground">
            {t("mpseo.sourcePrefix", { brand: c.brand })}{" "}
            <a
              href={c.source}
              rel="nofollow noopener noreferrer"
              target="_blank"
              className="underline"
            >
              {t("mpseo.sourceLink")}
            </a>
            {c.reviewed ? ` ${t("mpseo.sourceChecked", { date: c.reviewed })}` : "."}{" "}
            {t("mpseo.sourceNote")}
          </p>
        )}
      </section>

      <section className="mt-12">
        <h2 className="font-display text-xl font-bold sm:text-2xl">{t("mpseo.questions")}</h2>
        <dl className="mt-4 space-y-4">
          {c.faq.map(([q, a], i) => (
            <div key={i} className="card-surface p-4">
              <dt className="font-semibold">{q}</dt>
              <dd className="mt-1.5 text-sm leading-relaxed text-muted-foreground">{a}</dd>
            </div>
          ))}
        </dl>
      </section>

      <section className="mt-12">
        <h2 className="font-display text-sm font-bold uppercase tracking-widest text-muted-foreground">
          {t("mpseo.moreComparisons")}
        </h2>
        <div className="mt-3 flex flex-wrap gap-2">
          {MP_COMPARISONS.filter((x) => x.slug !== c.slug).map((x) => (
            <Link
              key={x.slug}
              to="/compare/marketplace/$competitor"
              params={{ competitor: x.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {t("mpseo.vs", { brand: x.brand })}
            </Link>
          ))}
          {MP_GUIDES.slice(0, 4).map((g) => (
            <Link
              key={g.slug}
              to="/marketplace/guides/$slug"
              params={{ slug: g.slug }}
              className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
            >
              {g.h1}
            </Link>
          ))}
          <Link
            to="/compare/hot"
            className="rounded-full border border-border bg-card px-3 py-1.5 text-sm text-muted-foreground hover:text-foreground"
          >
            {t("mpseo.cta.compareBoards")}
          </Link>
        </div>
      </section>
    </div>
  );
}
